import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FirearmActionClient, FirearmActionDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getFirearmActionClient = () => new FirearmActionClient(API_BASE_URL, axiosInstance);

// TanStack Query Hooks

// Cached lookup for dropdowns (all actions)
export const useFirearmActions = () =>
  useQuery({
    queryKey: ['firearmActions'],
    queryFn: async () => {
      const response = await getFirearmActionClient().searchFirearmActions({
        filters: { isActive: true },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Cached lookup for dropdowns (filtered by specific)
export const useFirearmActionsBySpecific = (firearmSpecificId: string | undefined) =>
  useQuery({
    queryKey: ['firearmActions', firearmSpecificId],
    queryFn: async () => {
      const response = await getFirearmActionClient().searchFirearmActions({
        filters: { firearmSpecificId },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Non-cached search for admin tables
export const useFirearmActionSearch = () => {
  const search = async (params: any) => {
    const response = await getFirearmActionClient().searchFirearmActions(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get by ID
export const useFirearmActionById = (id: string) =>
  useQuery({
    queryKey: ['firearmAction', id],
    queryFn: async () => {
      const response = await getFirearmActionClient().getFirearmActionById(id);
      return response?.data;
    },
    enabled: !!id,
  });

// Create mutation
export const useCreateFirearmAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FirearmActionDTO) => {
      await getFirearmActionClient().createFirearmAction(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmActions'] });
    },
  });
};

// Update mutation
export const useUpdateFirearmAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FirearmActionDTO }) => {
      await getFirearmActionClient().updateFirearmAction(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmActions'] });
    },
  });
};

// Delete mutation
export const useDeleteFirearmAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getFirearmActionClient().deleteFirearmAction(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmActions'] });
    },
  });
};